import React, { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Box, Typography } from '@mui/material';
import { formatChartTooltipTime } from '../../utils/serverTime';

export type ChartHoverTipItem = {
  name: string;
  value: React.ReactNode;
  color?: string;
};

export type ChartHoverTipData = {
  label: string;
  items: ChartHoverTipItem[];
};

type PayloadEntry = {
  name?: string;
  value?: unknown;
  color?: string;
  dataKey?: string;
};

function tipSignature(tip: ChartHoverTipData | null): string {
  if (!tip) return '';
  return JSON.stringify({
    label: tip.label,
    items: tip.items.map((i) => [i.name, String(i.value ?? ''), i.color ?? '']),
  });
}

function resolveTipLabel(
  payload: PayloadEntry[] | undefined,
  label: string | undefined,
  localeLang: string,
): string {
  const row = (payload?.[0] as PayloadEntry & { payload?: Record<string, unknown> })?.payload;
  if (row?.tooltipLabel != null && String(row.tooltipLabel).trim()) {
    return String(row.tooltipLabel);
  }
  if (row?.ts != null) {
    return formatChartTooltipTime(row.ts as string | number, localeLang);
  }
  if (row?.time_bucket != null) {
    return formatChartTooltipTime(row.time_bucket as string, localeLang);
  }
  return String(label ?? '');
}

/** Content de Recharts: captura el hover y no pinta nada dentro del chart. */
export function ChartHoverTipBridge({
  active,
  payload,
  label,
  formatItem,
  onTip,
  localeLang = 'es',
}: {
  active?: boolean;
  payload?: PayloadEntry[];
  label?: string;
  formatItem: (value: unknown, name: string) => React.ReactNode;
  onTip: (tip: ChartHoverTipData | null) => void;
  localeLang?: string;
}) {
  const onTipRef = useRef(onTip);
  const formatRef = useRef(formatItem);
  const lastSigRef = useRef<string | null>(null);
  onTipRef.current = onTip;
  formatRef.current = formatItem;

  const displayLabel = resolveTipLabel(payload, label, localeLang);

  // Firma estable de lo que muestra el tooltip (evita setState en loop por refs nuevas de Recharts).
  const signature = (() => {
    if (!active || !payload?.length) return '';
    const items = payload
      .filter((p) => p && p.value !== null && p.value !== undefined)
      .map((p) => {
        const name = String(p.name ?? p.dataKey ?? '');
        return {
          name,
          value: formatRef.current(p.value, name),
          color: p.color,
        };
      });
    if (!items.length) return '';
    return tipSignature({ label: displayLabel, items });
  })();

  useLayoutEffect(() => {
    if (signature === lastSigRef.current) return;
    lastSigRef.current = signature;
    if (!signature) {
      onTipRef.current(null);
      return;
    }
    if (!active || !payload?.length) {
      onTipRef.current(null);
      return;
    }
    const items = payload
      .filter((p) => p && p.value !== null && p.value !== undefined)
      .map((p) => {
        const name = String(p.name ?? p.dataKey ?? '');
        return {
          name,
          value: formatRef.current(p.value, name),
          color: p.color,
        };
      });
    onTipRef.current(items.length ? { label: displayLabel, items } : null);
  }, [signature, active, payload, displayLabel]);

  return null;
}

/**
 * Tooltip fuera del árbol del chart (portal), anclado al borde superior del
 * recuadro del gráfico. Así se ve entero aunque los padres tengan overflow.
 */
export function ChartHoverTipPortal({
  tip,
  anchorRef,
}: {
  tip: ChartHoverTipData | null;
  anchorRef: React.RefObject<HTMLElement | null>;
}) {
  const [box, setBox] = useState<{ left: number; top: number; maxWidth: number } | null>(null);
  const tipSig = tipSignature(tip);

  useLayoutEffect(() => {
    if (!tip || !anchorRef.current) {
      setBox((prev) => (prev == null ? prev : null));
      return;
    }
    const update = () => {
      const el = anchorRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      // Centrado sobre el borde superior del gráfico; el ancho lo define el contenido.
      const next = {
        left: r.left + r.width / 2,
        top: r.top - 6,
        maxWidth: Math.max(120, r.width - 16),
      };
      setBox((prev) => {
        if (
          prev
          && prev.left === next.left
          && prev.top === next.top
          && prev.maxWidth === next.maxWidth
        ) {
          return prev;
        }
        return next;
      });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [tip, tipSig, anchorRef]);

  if (!tip || !box || typeof document === 'undefined') return null;

  return createPortal(
    <Box
      sx={{
        position: 'fixed',
        left: box.left,
        top: box.top,
        maxWidth: box.maxWidth,
        width: 'max-content',
        transform: 'translate(-50%, -100%)',
        zIndex: 2100,
        pointerEvents: 'none',
        backgroundColor: 'rgba(18,18,20,0.96)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: '12px',
        px: 1.25,
        py: 0.75,
        boxShadow: '0 8px 24px rgba(0,0,0,0.45)',
      }}
    >
      {tip.label ? (
        <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 11, fontWeight: 600, mb: 0.35 }}>
          {tip.label}
        </Typography>
      ) : null}
      {tip.items.map((item) => (
        <Box
          key={item.name}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: 1.5,
            py: 0.15,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: item.color || '#2391FF',
                flexShrink: 0,
              }}
            />
            <Typography
              sx={{
                color: '#fff',
                fontSize: 11,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {item.name}
            </Typography>
          </Box>
          <Typography sx={{ color: '#fff', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
            {item.value}
          </Typography>
        </Box>
      ))}
    </Box>,
    document.body,
  );
}
